// Schrittlogik der Schadenmeldung als reine Funktionen — geteilt zwischen dem
// öffentlichen Wizard (/schaden-melden) und der Schadenmappe.
import { BIG_COPY } from "../../schadenmeldung-big/copy";
import {
  contactFilled,
  type BigAnswers,
  type BigStepKey,
} from "../../schadenmeldung-big/types";

export type SchrittZustand = "aktuell" | "ok" | "fehlt" | "leer";
export interface SchrittEintrag {
  key: BigStepKey;
  label: string;
  zustand: SchrittZustand;
}

/** Wer prüft: der öffentliche Wizard (Einwilligung) oder die Mappe (Meldedatum). */
export type PflichtKontext =
  | { art: "oeffentlich" }
  | {
      art: "intern";
      meldedatum: string | null;
      /** Kontakt gewählt? (Schritt „Kontakt“; ohne Angabe gilt er als gewählt.) */
      kontakt?: boolean;
    };

/** Geordnete Schrittliste zu den Antworten (identisch mit dem öffentlichen Wizard). */
export function schrittListe(a: BigAnswers): BigStepKey[] {
  const o: BigStepKey[] = ["type"];
  if (a.damageType === "gebaeude") {
    o.push(
      "geb_melder",
      "geb_when",
      "geb_where",
      "geb_art",
      "geb_hergang",
      "geb_massnahmen",
      "geb_verursacher",
      "geb_abrechnung",
      "geb_unterlagen",
    );
  } else if (a.damageType === "kfz") {
    o.push("kfz_schaeden", "kfz_melder", "kfz_when", "kfz_where", "kfz_art");
    if (a.kfzSchaeden.includes("eigenes")) o.push("kfz_eigenes");
    if (a.kfzSchaeden.includes("fremdes")) o.push("kfz_fremdes");
    o.push("kfz_hergang");
    if (a.kfzSchaeden.includes("person")) o.push("kfz_person");
    if (a.kfzSchaeden.includes("sonstige")) o.push("kfz_sachen");
    o.push("kfz_unterlagen");
  } else if (a.damageType === "sonstiges") {
    o.push(
      "son_betroffen",
      "son_melder",
      "son_when",
      "son_where",
      "son_details",
      "son_unterlagen",
    );
  }
  if (a.damageType) o.push("abschluss", "summary");
  return o;
}

const MELDER_SCHRITTE: readonly BigStepKey[] = ["geb_melder", "kfz_melder", "son_melder"];
const WO_SCHRITTE: readonly BigStepKey[] = ["geb_where", "kfz_where", "son_where"];
const WANN_SCHRITTE: readonly BigStepKey[] = ["geb_when", "kfz_when", "son_when"];

/**
 * Schrittfolge der Schadenmappe (intern, 13.10.2026): Kontakt → Schadenart →
 * Risiko & Vertrag → Wo → Wann → die übrigen Schritte der Schadenart in der
 * Reihenfolge des Wizards → Schadenmelder → Abschluss → Prüfen. Der
 * öffentliche Wizard bleibt bei `schrittListe`.
 */
export function schrittListeIntern(a: BigAnswers): BigStepKey[] {
  const o: BigStepKey[] = ["kontakt", "type", "risiko"];
  if (!a.damageType) return o;
  const oeffentlich = schrittListe(a);
  const nur = (menge: readonly BigStepKey[]) => oeffentlich.filter((k) => menge.includes(k));
  const fest: readonly BigStepKey[] = [
    "type",
    "abschluss",
    "summary",
    ...MELDER_SCHRITTE,
    ...WO_SCHRITTE,
    ...WANN_SCHRITTE,
  ];
  o.push(
    ...nur(WO_SCHRITTE),
    ...nur(WANN_SCHRITTE),
    ...oeffentlich.filter((k) => !fest.includes(k)),
    ...nur(MELDER_SCHRITTE),
    "abschluss",
    "summary",
  );
  return o;
}

/** Abweichende Namen in der Seitenleiste der Mappe. */
const LABEL_INTERN: Partial<Record<BigStepKey, string>> = {
  kontakt: "Kontakt",
  risiko: "Risiko & Vertrag",
  geb_melder: "Schadenmelder",
  kfz_melder: "Schadenmelder",
  son_melder: "Schadenmelder",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Pflichtangaben des Schritts erfüllt? (`canAdvance` des Wizards.) */
export function pflichtErfuellt(
  step: BigStepKey,
  a: BigAnswers,
  kontext: PflichtKontext,
): boolean {
  switch (step) {
    case "kontakt":
      return kontext.art !== "intern" || kontext.kontakt !== false;
    case "type":
      return a.damageType !== null;
    case "kfz_schaeden":
      return a.kfzSchaeden.length > 0;
    case "son_betroffen":
      return a.sonBetroffen !== null;
    case "geb_melder":
    case "kfz_melder":
    case "son_melder":
      return (
        (a.melderVorname.trim().length > 0 || a.melderName.trim().length > 0) &&
        EMAIL_RE.test(a.melderEmail.trim())
      );
    case "geb_when":
    case "kfz_when":
    case "son_when":
      return a.schadenDatum.trim().length > 0 || a.entdecktAm.trim().length > 0;
    case "geb_where":
      return (
        a.gebOrt.trim().length > 0 ||
        a.gebStrasse.trim().length > 0 ||
        a.gebEinheiten.length > 0
      );
    case "kfz_where":
      return a.kfzOrt.trim().length > 0;
    case "geb_art":
      return a.gebSchadenart !== null;
    case "kfz_art":
      return a.kfzArt !== null;
    case "geb_hergang":
      return a.gebHergang.trim().length > 0;
    case "kfz_person":
      return a.kfzPersonen.some((p) => p.name.trim().length > 0);
    case "son_details":
      if (a.sonBetroffen === "sache") return a.sonSacheBeschreibung.trim().length > 0;
      if (a.sonBetroffen === "mensch") return contactFilled(a.sonMenschContact);
      if (a.sonBetroffen === "sonstiges") return a.sonSonstHergang.trim().length > 0;
      return true;
    case "abschluss":
      return kontext.art === "oeffentlich"
        ? a.consentGiven === true
        : (kontext.meldedatum ?? "").trim().length > 0;
    default:
      return true;
  }
}

export function schrittLabel(key: BigStepKey): string {
  return LABEL_INTERN[key] ?? (BIG_COPY.steps as Record<string, string>)[key] ?? key;
}

/**
 * Seitenleisten-Einträge mit Zustand (Spec §1): `aktuell`; ein Schritt, den man
 * schon gesehen hat (besucht oder vor dem aktuellen), ist `ok` bei erfüllter und
 * `fehlt` bei unerfüllter Pflicht; ein nie gesehener Schritt hinter dem
 * aktuellen bleibt `leer` — auch wenn er keine Pflicht hat, sonst stünde ein
 * Haken vor Schritten, die niemand angeschaut hat. `summary`/`done` haben keine
 * Pflicht und sind nie `fehlt`.
 */
export function schrittEintraege(
  a: BigAnswers,
  aktuell: BigStepKey,
  besucht: ReadonlySet<BigStepKey>,
  kontext: PflichtKontext,
): SchrittEintrag[] {
  const liste = kontext.art === "intern" ? schrittListeIntern(a) : schrittListe(a);
  const aktuellIdx = liste.indexOf(aktuell);
  return liste.map((key, i) => {
    let zustand: SchrittZustand;
    const gesehen = besucht.has(key) || (aktuellIdx >= 0 && i < aktuellIdx);
    if (key === aktuell) zustand = "aktuell";
    else if (!gesehen) zustand = "leer";
    else if (pflichtErfuellt(key, a, kontext)) zustand = "ok";
    else zustand = "fehlt";
    return { key, label: schrittLabel(key), zustand };
  });
}

/** Nächster Schritt in der Folge der Mappe (`schrittListeIntern`). */
export function naechsterSchritt(a: BigAnswers, aktuell: BigStepKey): BigStepKey | null {
  const liste = schrittListeIntern(a);
  const i = liste.indexOf(aktuell);
  return i >= 0 && i < liste.length - 1 ? liste[i + 1] : null;
}

export function vorigerSchritt(a: BigAnswers, aktuell: BigStepKey): BigStepKey | null {
  const liste = schrittListeIntern(a);
  const i = liste.indexOf(aktuell);
  return i > 0 ? liste[i - 1] : null;
}

/** Erster Schritt mit fehlender Pflichtangabe, sonst "summary". */
export function startSchritt(
  a: BigAnswers,
  intern: { meldedatum: string | null },
): BigStepKey {
  const kontext: PflichtKontext = { art: "intern", meldedatum: intern.meldedatum };
  return schrittListeIntern(a).find((k) => !pflichtErfuellt(k, a, kontext)) ?? "summary";
}

export const MELDUNG_BEREICHE = ["meldung", "bearbeitung", "regulierung"] as const;
export type Bereich = (typeof MELDUNG_BEREICHE)[number];

export function istBereich(x: string | undefined): x is Bereich {
  return (MELDUNG_BEREICHE as readonly string[]).includes(x ?? "");
}
